/**
 * Runtime configuration for the wallet SPA.
 *
 * The SPA ships as a single static bundle deployed to many environments
 * (devnet / staging / prod). Rather than baking environment URLs into the
 * bundle at build time, config is resolved at runtime in this order:
 *
 *   1. `window.__RUNTIME_CONFIG__` — injected by the container at deploy time
 *      via a mounted `config.js` (see the `cef-wallet-ui` Helm chart's
 *      ConfigMap). This is how the K3S deployment configures each environment
 *      with one image.
 *   2. `import.meta.env.VITE_*` — build-time fallback, used for local `pnpm dev`
 *      and the Playwright e2e suite (see `.env.development`).
 *
 * There is intentionally NO hardcoded production default: a misconfigured
 * deploy must fail loudly rather than silently point at someone else's API.
 */

export interface RuntimeConfig {
  /** Base URL of the wallet-api backend, e.g. `https://wallet-api.example.com`. */
  walletApiBaseUrl: string;
  /** Deployment environment label, surfaced in diagnostics. Optional. */
  appEnv?: string;
  /**
   * Whole origins whose delegation requests auto-approve without a consent
   * prompt — the wallet's own consumer apps for this environment.
   *
   * NOT the same list as wallet-api's `WALLET_API_EMBED_TOP_ORIGINS`, though
   * it is drawn from the same population. That one says who may RUN a
   * ceremony; this one says who SKIPS CONSENT. A cross-site consumer belongs
   * in the first and not the second — production prompts a cross-site
   * consumer, so auto-approving one in dev would measure a consent behaviour
   * production does not have.
   *
   * Accepts an array; a comma-separated string is split, because the adjacent
   * embed allowlist is a CSV and copying that shape across is the obvious
   * mistake.
   *
   * Absent or empty means only the wallet's own origin is first-party, so every
   * consumer is prompted. That is the safe direction and it is deliberate: the
   * trust boundary must be stated, never inferred from the wallet's hostname
   * (CEF-AI/wallet#74).
   */
  firstPartyOrigins?: string[];
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Partial<RuntimeConfig>;
  }
}

function readEnv(key: string): string | undefined {
  const env = (typeof import.meta !== 'undefined' && (import.meta as any).env) || {};
  const value = env[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The build-time fallback is one env var, so the list arrives comma-separated.
 * The runtime `config.js` path passes a real array and needs no parsing.
 * Returns undefined rather than `[]` for an absent value: "not configured" and
 * "configured empty" are the same decision here, but undefined reads as the
 * former at every call site.
 */
function parseOriginList(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  const entries = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return entries.length > 0 ? entries : undefined;
}

/**
 * The first-party origin list, resolved independently of the rest of the
 * config (CEF-AI/wallet#74).
 *
 * Separate from `getRuntimeConfig()` on purpose: that function throws when
 * `walletApiBaseUrl` is absent, which is right for a deployment and wrong as a
 * precondition for answering "which origins skip the consent prompt". Coupling
 * them would make bridge construction fail on a value it does not use, and
 * would make every bridge test configure an API URL it never calls.
 *
 * Absent means absent. It must NOT fall back to a default list — an
 * unconfigured environment prompts for everything, which is the safe direction
 * and the one `isFirstPartyOrigin` warns about.
 */
export function getFirstPartyOrigins(): string[] | undefined {
  const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  const fromRuntime = normaliseOriginList(runtime.firstPartyOrigins);
  if (fromRuntime) return fromRuntime;
  // A runtime value that was absent, empty, or unusable falls through to the
  // build-time env var — including an explicitly empty `[]`, which is
  // pre-existing behaviour the old `.length` gate had too. Both paths end at
  // `undefined` when neither supplies anything, which is the fail-closed case.
  return parseOriginList(readEnv('VITE_WALLET_FIRST_PARTY_ORIGINS'));
}

/**
 * Accept what a deploy actually hands us, and refuse what it cannot mean.
 *
 * `config.js` is generated by a Helm chart, not type-checked, and the adjacent
 * `WALLET_API_EMBED_TOP_ORIGINS` is a COMMA-SEPARATED STRING. Copying that
 * shape across is the obvious mistake, and the original code invited it: it
 * gated on `.length`, which a non-empty string also has, so a CSV flowed
 * straight through as if it were an array. Nothing failed at boot. The first
 * symptom would have been `firstPartyOrigins.some is not a function` thrown
 * inside the consent path, at delegation time, long after a green deploy —
 * and only for a user who reached that path.
 *
 * So a string is now SPLIT rather than rejected: the operator's intent is
 * unambiguous and a runtime crash is a poor way to spell "wrong quoting". A
 * value that is neither a string nor an array is dropped with a warning, since
 * there is nothing to interpret. Non-string array entries are dropped
 * individually — one bad entry must not discard the rest of the list.
 *
 * Found by wallet-devops while building the chart change that carries this
 * key, before it ever ran.
 */
function normaliseOriginList(value: unknown): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return parseOriginList(value);
  if (!Array.isArray(value)) {
    console.warn(
      `[wallet] ignoring firstPartyOrigins: expected an array of origins (or a comma-separated ` +
        `string), got ${typeof value}. Falling back to VITE_WALLET_FIRST_PARTY_ORIGINS if it is ` +
        `set; if it is not, every consumer will be prompted for consent.`,
    );
    return undefined;
  }
  const entries: string[] = [];
  const dropped: string[] = [];
  for (const entry of value) {
    if (typeof entry === 'string' && entry.trim().length > 0) entries.push(entry.trim());
    else dropped.push(typeof entry === 'string' ? JSON.stringify(entry) : String(entry));
  }
  if (dropped.length > 0) {
    // Name what was dropped. A silently discarded entry means exactly ONE
    // consumer starts getting a consent prompt, with no signal and no change
    // to its own code — the operator sees "consent is broken for vault but
    // fine for marketplace" and has nothing to attribute it to. That is the
    // failure shape this whole key exists to remove, so the warning has to be
    // self-attributing rather than merely present.
    console.warn(
      `[wallet] firstPartyOrigins: ignored ${dropped.length} malformed ` +
        `${dropped.length === 1 ? 'entry' : 'entries'} (${dropped.join(', ')}). ` +
        `Those origins will be prompted for consent; the rest of the list still applies.`,
    );
  }
  return entries.length > 0 ? entries : undefined;
}

let cached: RuntimeConfig | null = null;

/**
 * Resolve the SPA's runtime configuration. Throws if the wallet-api base URL is
 * absent from both the runtime config object and the build-time env — a
 * deployment without it is non-functional, so failing here is correct.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (cached) return cached;

  const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  const walletApiBaseUrl = runtime.walletApiBaseUrl || readEnv('VITE_WALLET_API');

  if (!walletApiBaseUrl) {
    throw new Error(
      'wallet-api base URL is not configured. Set window.__RUNTIME_CONFIG__.walletApiBaseUrl ' +
        '(via the deployed config.js) or VITE_WALLET_API (local dev / e2e).',
    );
  }

  cached = {
    walletApiBaseUrl,
    appEnv: runtime.appEnv || readEnv('VITE_APP_ENV'),
    firstPartyOrigins: getFirstPartyOrigins(),
  };
  return cached;
}
